// Analytics - dual destination (PostHog + Encharge).
// Replaces the previous trackingService.js (GTM/dataLayer) and
// metaPixelService.js (Meta Pixel + CAPI). Function names are preserved
// so import sites only need to swap the path.
//
// Event naming: snake_case, action-oriented (sign_up, create_group, etc.).
// PostHog dashboards reference these names - do not rename without updating
// dashboards.
//
// Every named track helper funnels through `capture()`, which fans the event
// out to PostHog (product analytics) AND Encharge (marketing CRM). $pageview
// has its own helper below and bypasses Encharge by design - too noisy for
// a CRM. Encharge calls are fire-and-forget so PostHog timing is unaffected.

import posthog from 'posthog-js';
import { supabase } from '../lib/supabase';
import { trackEvent as enchargeTrackEvent } from './enchargeService';

const isPostHogReady = () =>
  typeof window !== 'undefined' && posthog && typeof posthog.capture === 'function';

// Events that should stay PostHog-only. Add here if a noisy or
// developer-facing event is ever introduced via capture(). Ad telemetry is
// high-frequency and product-facing, so it never goes to the marketing CRM.
const SKIP_ENCHARGE_EVENTS = new Set([
  'rewarded_gate_shown',
  'rewarded_ad_watched',
  'rewarded_reward_earned',
  'rewarded_gate_dismissed',
  'rewarded_unavailable_passthrough',
  'rewarded_gate_upgrade_click',
  'adsense_slot_requested',
  'adsense_slot_filled',
  'adsense_slot_unfilled',
]);

const mirrorToEncharge = (eventName, properties) => {
  if (SKIP_ENCHARGE_EVENTS.has(eventName)) return;
  // Resolve the authenticated user lazily; no await on the caller path.
  supabase.auth.getUser().then(({ data }) => {
    const user = data?.user;
    if (!user?.email) return;
    enchargeTrackEvent(user, eventName, properties);
  }).catch(() => { /* swallowed - logged inside enchargeService */ });
};

// `send_instantly: true` bypasses the batching queue and POSTs immediately.
// Without it, events that fire right before a navigation (create_group →
// navigate, schedule_round_* → navigate, etc.) get dropped when the page
// unloads before the next flush. Confirmed empirically: those tiles read 0
// in PostHog while $pageview and onboarding_step_completed worked fine.
const capture = (eventName, properties = {}) => {
  if (!isPostHogReady()) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] PostHog not ready - event dropped:', eventName, properties);
    }
    return;
  }
  posthog.capture(eventName, properties, { send_instantly: true });
  if (import.meta.env.DEV) {
    console.log('[analytics]', eventName, properties);
  }
  mirrorToEncharge(eventName, properties);
};

// Generic - for new ad-hoc events that don't yet have a named helper.
export const trackEvent = (eventName, properties = {}) => capture(eventName, properties);

// Auth
export const trackSignUp = (userId) =>
  capture('sign_up', { user_id: userId });

export const trackSignUpEmailVerified = (userId) =>
  capture('sign_up_email_verified', { user_id: userId });

export const trackSignIn = (userId) =>
  capture('sign_in', { user_id: userId });

export const trackSignOut = (userId) =>
  capture('sign_out', { user_id: userId });

// Groups
export const trackCreateGroup = (userId, groupId) =>
  capture('create_group', { user_id: userId, group_id: groupId });

export const trackJoinGroup = (userId, groupId) =>
  capture('join_group', { user_id: userId, group_id: groupId });

// Rounds - smart schedule
export const trackScheduleRoundSmartInvitesSent = (userId, groupId, roundId) =>
  capture('schedule_round_smart_invites_sent', {
    user_id: userId,
    group_id: groupId,
    round_id: roundId,
  });

export const trackScheduleRoundSmartResultsReturned = (userId, groupId) =>
  capture('schedule_round_smart_results_returned', {
    user_id: userId,
    group_id: groupId,
  });

// Rounds - quick schedule
export const trackScheduleRoundQuick = (userId, groupId, roundId) =>
  capture('schedule_round_quick', {
    user_id: userId,
    group_id: groupId,
    round_id: roundId,
  });

// Rounds - solo schedule (no group invite)
export const trackScheduleRoundSmartSolo = (userId, roundId) =>
  capture('schedule_round_smart_solo', { user_id: userId, round_id: roundId });

export const trackScheduleRoundQuickSolo = (userId, roundId) =>
  capture('schedule_round_quick_solo', { user_id: userId, round_id: roundId });

// Availability
export const trackSetAvailability = (userId) =>
  capture('set_availability', { user_id: userId });

export const trackUpdateAvailability = (userId) =>
  capture('update_availability', { user_id: userId });

export const trackAvailabilityPrefSetAvailable = (userId) =>
  capture('availability_pref_set_available', { user_id: userId });

export const trackAvailabilityPrefSetBusy = (userId) =>
  capture('availability_pref_set_busy', { user_id: userId });

// Round responses
export const trackRoundResponseAccept = (userId, roundId) =>
  capture('round_response_accept', { user_id: userId, round_id: roundId });

export const trackRoundResponseDecline = (userId, roundId) =>
  capture('round_response_decline', { user_id: userId, round_id: roundId });

// Pageviews - App.jsx wires this up via useLocation.
// PostHog convention: $pageview is reserved/special; the SDK feeds it
// into trends, replays, and pathfinder automatically.
export const trackPageView = (pagePath) => {
  if (!isPostHogReady()) return;
  posthog.capture('$pageview', {
    $current_url: typeof window !== 'undefined' ? window.location.href : pagePath,
    page_path: pagePath,
  });
};

// Onboarding
export const trackOnboardingStepCompleted = (userId, step, extra = {}) =>
  capture('onboarding_step_completed', { user_id: userId, step, ...extra });

export const trackOnboardingCompleted = (userId, extra = {}) =>
  capture('onboarding_completed', { user_id: userId, ...extra });

// Ads / monetisation (PostHog-only; see SKIP_ENCHARGE_EVENTS).
// `feature` is the gated action ('smart_schedule' | 'planned_round').
// The iOS rewarded ad-gate funnels through these.
export const trackRewardedGateShown = (feature) =>
  capture('rewarded_gate_shown', { feature });

export const trackRewardedAdWatched = (feature) =>
  capture('rewarded_ad_watched', { feature });

export const trackRewardedRewardEarned = (feature) =>
  capture('rewarded_reward_earned', { feature });

export const trackRewardedGateDismissed = (feature) =>
  capture('rewarded_gate_dismissed', { feature });

// Ad not ready / failed to load: the action is allowed through (fail-open).
export const trackRewardedUnavailablePassthrough = (feature) =>
  capture('rewarded_unavailable_passthrough', { feature });

export const trackRewardedGateUpgradeClick = (feature) =>
  capture('rewarded_gate_upgrade_click', { feature });

// Web AdSense slot lifecycle. `status` is 'requested' | 'filled' | 'unfilled';
// `variant` is the unit type ('siderail' | 'infeed').
export const trackAdsenseSlot = (status, variant) =>
  capture(`adsense_slot_${status}`, { variant });
